const _ = require('lodash');
const { Observable } = require('rxjs');

const STATUS_INIT = 'init';
const STATUS_PENDING = 'pending';
const STATUS_DONE = 'done';

const getDebounceOptions = (options) => ({
  debounce: !!_.get(options, 'debounce', false),
  debounceWait: _.get(options, 'debounceWait', 100),
  debounceMaxWait: _.get(options, 'debounceMaxWait'),
});

function observe(bee, namespace, className, options = {}) {
  const fullClassName = `${namespace}.${className}`;
  console.log(`Preparing observable for ${fullClassName}`);

  const debounceOptions = getDebounceOptions(options);
  const useCreateMany = !!_.get(options, 'useCreateMany', false);
  const processItemsFn = _.get(options, 'processItemsFn', _.identity);
  if (!_.isFunction(processItemsFn)) {
    throw 'options.processItemsFn must be a function!';
  }
  const mapInstanceKeysFn = _.get(options, 'mapInstanceKeysFn', instances => _.mapKeys(instances, 'id'));
  if (!_.isFunction(mapInstanceKeysFn)) {
    throw 'options.mapInstanceKeysFn must be a function!';
  }

  return new Observable(subscriber => {
    console.log(`Subscribing to ${namespace}.${className}`);

    let fetchPr = Promise.resolve();

    // We store the values as a map of instanceId to value for easy access and editing
    // But the values are returned as an array of the values
    let items = {};
    let status = STATUS_INIT;

    // Debounce the call to the subscriber.next function if required
    // Note: for some reason, cannot use `subscriber.next` (a function) directly, so we need
    // to wrap in an explicit function
    const nextFn = debounceOptions.debounce
      ? _.debounce(
        newItems => subscriber.next(newItems),
        debounceOptions.debounceWait,
        _.pickBy({ maxWait: debounceOptions.debounceMaxWait })
      )
      : newItems => subscriber.next(newItems);

    const subscriberNext = (newStatus, newItems, flushNow = false) => {
      items = newItems || items;
      status = newStatus || status;

      newItems = processItemsFn(_.values(items));
      newItems.status = status;

      nextFn(newItems);

      if (debounceOptions.debounce && flushNow) {
        nextFn.flush();
      }
    };

    const subscriberError = (e) => {
      subscriber.error(e);
      status = STATUS_DONE;
    };

    const deleteLocalItem = (id) => {
      console.log(`Item ${id} has been deleted`);
      _.unset(items, id);
      subscriberNext(status, items);
    };

    const updateLocalItem = (id) => {
      console.log(`Item ${id} has been changed`);
      fetchPr = fetchPr
        .then(() => subscriberNext(STATUS_PENDING))
        .then(() => bee.storage.getClassInstanceByClassNameAndId(namespace, className, id))
        .then(instance => subscriberNext(STATUS_DONE, _.set(items, instance.id, instance)))
        .catch(subscriberError)
      ;
    };

    const addLocalItems = (ids) => {
      ids = _.compact(_.castArray(ids));
      console.log(`${_.size(ids)} items have been created`);
      fetchPr = fetchPr
        .then(() => subscriberNext(STATUS_PENDING))
        .then(() => bee.storage.getManyClassInstanceByClassNameAndId(namespace, className, ids))
        .then(mapInstanceKeysFn)
        .then(instances => subscriberNext(STATUS_DONE, _.assignIn(items, instances)))
        .catch(subscriberError)
      ;
    };

    // Prepare the callbacks for the storage events
    const callbackIds = [
      bee.reactions.setCallback(`storage:destroy.${fullClassName}`, r => deleteLocalItem(r.details.id)),
      bee.reactions.setCallback(`storage:mutate.${fullClassName}`, r => updateLocalItem(r.details.id)),
      useCreateMany
        ? bee.reactions.setCallback(`storage:createMany.${fullClassName}`, r => addLocalItems(r.details.ids))
        : bee.reactions.setCallback(`storage:create.${fullClassName}`, r => addLocalItems(r.details.id)),
    ];

    // Perform the initial fetch, where we get all the instances
    fetchPr = fetchPr
      .then(() => subscriberNext(STATUS_PENDING, undefined, true))
      .then(() => bee.storage.getClassInstancesByClassName(namespace, className))
      .then(mapInstanceKeysFn)
      .then(instances => subscriberNext(STATUS_DONE, instances, true))
      .catch(e => {
        subscriber.error(e);
        status = STATUS_DONE;
      });

    return () => {
      _.forEach(callbackIds, bee.reactions.removeCallback);
      items = {};
      status = STATUS_INIT;
      console.log(`Unsubscribed from ${namespace}.${className}`);
    };
  });
}

function observeQuery(
  bee,
  namespace,
  queryName,
  className,
  queryParams
) {
  return observeQueryWithOptions(bee, namespace, queryName, className, {}, ...queryParams);
}

function observeQueryWithOptions(
  bee,
  namespace,
  queryName,
  className,
  options = {},
  ...queryParams
) {
  const fullClassName = `${namespace}.${className}`;
  const fullQueryName = `${namespace}.${queryName}`;
  console.log(`Preparing observable for query ${fullQueryName}`);

  const debounceOptions = getDebounceOptions(options);
  const useCreateMany = !!_.get(options, 'useCreateMany', false);
  const processItemsFn = _.get(options, 'processItemsFn', _.identity);
  if (!_.isFunction(processItemsFn)) {
    throw 'options.processItemsFn must be a function!';
  }
  const mapInstanceKeysFn = _.get(options, 'mapInstanceKeysFn', instances => _.mapKeys(instances, 'id'));
  if (!_.isFunction(mapInstanceKeysFn)) {
    throw 'options.mapInstanceKeysFn must be a function!';
  }

  return new Observable(subscriber => {
    console.log(`Subscribing to query ${fullQueryName}`);

    // In order to serialize requests
    let fetchPr = Promise.resolve();

    // We store the values as a map of instanceId to value for easy access and editing
    // But the values are returned as an array of the values
    let items = {};
    let status = STATUS_INIT;

    const subscriberNext = (newStatus, newItems) => {
      items = newItems || items;
      status = newStatus || status;

      newItems = processItemsFn(_.values(items));
      newItems.status = status;

      subscriber.next(newItems);
    };

    const subscriberError = (e) => {
      subscriber.error(e);
      status = STATUS_DONE;
    };

    const deleteLocalItem = (id) => {
      console.log(`Removing item ${id}`);
      _.unset(items, id);
      subscriberNext(status, items);
    };

    // At the moment, we cannot know whether a mutated or created item fits in the query results
    // So we need to fetch everything.
    const fetchItemsNow = () => {
      fetchPr = fetchPr
        .then(() => subscriberNext(STATUS_PENDING))
        .then(() => bee.storage.executeQuery(namespace, queryName, queryParams || []))
        .then(mapInstanceKeysFn)
        .then(instances => subscriberNext(STATUS_DONE, instances))
        .catch(subscriberError)
      ;
    };

    const fetchItems = debounceOptions.debounce
      ? _.debounce(
        fetchItemsNow,
        debounceOptions.debounceWait,
        _.pickBy({ maxWait: debounceOptions.debounceMaxWait })
      )
      : fetchItemsNow;

    // Prepare the callbacks for the storage events
    const callbackIds = [
      bee.reactions.setCallback(`storage:destroy.${fullClassName}`, r => deleteLocalItem(r.details.id)),
      bee.reactions.setCallback(`storage:mutate.${fullClassName}`, fetchItems),
      useCreateMany
        ? bee.reactions.setCallback(`storage:createMany.${fullClassName}`, fetchItems)
        : bee.reactions.setCallback(`storage:create.${fullClassName}`, fetchItems),
    ];

    // Perform the initial fetch, where we get all the instances
    fetchPr = fetchPr
      .then(fetchItemsNow);

    return () => {
      _.forEach(callbackIds, bee.reactions.removeCallback);
      items = {};
      status = STATUS_INIT;
      console.log(`Unsubscribed from query ${fullQueryName}`);
    };
  });
}

function observeAggregateQuery(
  bee,
  namespace,
  queryName,
  className,
  options,
  ...queryParams
) {
  options = _.assignIn(
    {
      mapInstanceKeysFn: _.identity,
    },
    options,
  );

  return observeQueryWithOptions(
    bee,
    namespace,
    queryName,
    className,
    options,
    ...queryParams
  );
}


const processQueries = (namespace, queries) => {
  if (!_.isArray(queries) || _.isEmpty(queries)) {
    throw `filters must be an array with at least one item!`;
  }

  queries = _.map(queries, (q, index) => {
    if (!q.queryName) {
      throw `filter #${index} must have a queryName value`;
    }

    return _.assignIn(
      {
        // Defaults
        allowed: _.stubTrue,
        parameters: () => [],
      },
      q,
      { fullQueryName: `${namespace}.${q.queryName}`},
    );
  });

  return queries;
};

const determineQuery = (queries, ...args) => {
  for (let q of queries) {
    if (q.allowed(...args)) {
      return q;
    }
  }

  // If not query can be found, use the last as a fallback
  return _.last(queries);
};

class FilteredQueryObservable extends Observable {
  constructor(changeParametersFn, subscriber) {
    super(subscriber);
    this._changeParametersFn = changeParametersFn || _.identity;
  }

  changeParameters(...p) {
    return this._changeParametersFn(...p);
  }
}

function observeFilteredQuery(bee, namespace, className, queries, options = {}, ...params) {
  const {
    debounceChangeParams = false,
    debounceChangeParamsWait = 100
  } = options;
  queries = processQueries(namespace, queries);
  const names = _.join(
    _.map(queries, q => _.get(q, 'name') || _.get(q, 'fullQueryName')),
    ','
  );
  console.log(`Preparing observable for filtered queries ${names}`);

  let subscription;
  let subscriber;

  let changeParameters = (...params) => {
    if (!subscriber) {
      throw `observeFilteredQuery needs to be susbcribed to in order to change parameters!`;
    }

    if (subscription) {
      subscription.unsubscribe();
      subscription = undefined;
    }

    const { queryName, parameters: getParameters } = determineQuery(queries, ...params);
    params = getParameters(...params);
    const query$ = observeQueryWithOptions(bee, namespace, queryName, className, options, ...params);

    subscription = query$
      .subscribe(
        value => subscriber.next(value),
        error => subscriber.error(error),
      )
    ;
  };

  if (debounceChangeParams) {
    changeParameters = _.debounce(changeParameters, debounceChangeParamsWait);
  }

  return new FilteredQueryObservable(changeParameters, function(newSubscriber) {
    console.log(`Subscribing to filtered queries ${names}`);
    subscriber = newSubscriber;

    // Perform the initial query
    this.changeParameters(...params);

    return () => {
      if (subscription) {
        subscription.unsubscribe();
        subscription = undefined;
      }

      subscriber = undefined;

      console.log(`Unsubscribed from filtered queries ${names}`);
    };
  });
}

function observeFilteredAggregateQuery(bee, namespace, className, queries, options = {}, ...params) {
  options = _.assignIn(
    {
      mapInstanceKeysFn: _.identity,
    },
    options,
  );

  return observeFilteredQuery(
    bee,
    namespace,
    className,
    queries,
    options,
    ...params
  );
}

/**
 * @namespace ContentObserver
 */
module.exports = {
  /**
   * @function observe
   * @memberof ContentObserver
   * @description Observes the instances of a specified class (by namespace and name).
   *
   * The initial subscription will result in the fetching all the values for that class.
   * Thereafter, storage events will maintain the observer class.
   *
   * Important: this observer fetches ALL the instances of a klass. Therefore it should be
   * used with classes that have a limited or controlled number of instances.
   *
   * @param {Object} bee - the bee object to use.
   * @param {String} namespace - the namespace of the class to observe.
   * @param {String} className - the name of the class to observer.
   * @param {Object} options - optional settings for the observing.
   * @param {Boolean} options.debounce - whether updates or new instances events are debounces to prevent too many requests.
   * (Default: false)
   * @param {Number} options.debounceWait - the wait time, in milliseconds, for debouncing. (Default: 100)
   * @param {Number} options.debounceMaxWait - the maximum time, in milliseconds, the callback is to be delayed. (Default: not set).
   * @param {Boolean} useCreateMany - hooks to 'storage.createMany' events instead of 'storage.create'
   * @param {Function} processItemsFn - function to process ALL items after events
   *
   * @example
   * const data$ = ContentObserver.observe(myBee, 'test', 'data');
   *
   * data$.subscribe(
   *   (items) => {
   *     // receive all the items always. Subsequent calls will contain the new data
   *   }
   * );
   */
  observe,

  /**
   * @function observeQuery
   * @memberof ContentObserver
   * @description Observes the instances that match a query with parameters.
   *
   * The initial subscription will result in the fetching all the values for that query.
   * Thereafter, storage events will maintain the observer class.
   *
   * Important: this observer fetches ALL the instances returned by a query. Therefore it should be
   * used with queries that have a limited or controlled number of instances.
   *
   * @param {Object} bee - the bee object to use.
   * @param {String} namespace - the namespace of the query to observe.
   * @param {String} queryName - the name of the query to observer.
   * @param {String} className - the name of the class to used by the query. This information is needed in order to
   * register proper storage events.
   * @param {Array} ...queryParams - optional parameters to pass to the query.
   *
   * @example
   * const data$ = ContentObserver.observeQuery(myBee, 'test', 'queryByAge', 'data', 18, 65);
   *
   * data$.subscribe(
   *   (items) => {
   *     // receive all the items always. Subsequent calls will contain the changed data
   *     console.log('items is an array of all items', items);
   *     console.log('item.status is the status of the current query', items.status)
   *   }
   * );
   */
  observeQuery,

  /**
   * @function observeQueryWithOptions
   * @memberof ContentObserver
   * @description Observes the instances that match a query with parameters.
   *
   * The initial subscription will result in the fetching all the values for that query.
   * Thereafter, storage events will maintain the observer class.
   *
   * Important: this observer fetches ALL the instances returned by a query. Therefore it should be
   * used with queries that have a limited or controlled number of instances.
   *
   * @param {Object} bee - the bee object to use.
   * @param {String} namespace - the namespace of the query to observe.
   * @param {String} queryName - the name of the query to observer.
   * @param {String} className - the name of the class to used by the query. This information is needed in order to
   * register proper storage events.
   * @param {Object} options - optional settings for the query.
   * @param {Boolean} options.debounce - whether updates or new instances events are debounces to prevent too many requests.
   * (Default: false)
   * @param {Number} options.debounceWait - the wait time, in milliseconds, for debouncing. (Default: 100)
   * @param {Number} options.debounceMaxWait - the maximum time, in milliseconds, the callback is to be delayed. (Default: not set).
   * @param {Boolean} options.useCreateMany - hooks to 'storage.createMany' events instead of 'storage.create'
   * @param {Function} options.processItemsFn - function to process ALL items after events
   * @param {Function} options.mapInstanceKeysFn - function used to process the ID of each instance or group
   * (for aggregate queries, such as counts or stats).
   * @param {Array} ...queryParams -  optional parameters to pass to the query.
   *
   * @return {Observable} observable - an observable that can be subscribed to for updates instances.
   *
   * @example
   * const data$ = ContentObserver.observeQueryWithOptions(
   *   myBee,
   *   'test',
   *   'queryByAge',
   *   'data',
   *   { debounce: true, debounceWait: 5000 },
   *   18,
   *   65);
   *
   * data$.subscribe(
   *   (items) => {
   *     // receive all the items always. Subsequent calls will contain the changed data
   *     console.log('items is an array of all items', items);
   *     console.log('item.status is the status of the current query', items.status)
   *   }
   * );
   */
  observeQueryWithOptions,

  /**
   * @function observeAggregateQuery
   * @memberof ContentObserver
   * @description Observes the results of a count or stats query with parameters.
   *
   * @param {Object} bee - the bee object to use.
   * @param {String} namespace - the namespace of the query to observe.
   * @param {String} queryName - the name of the query to observer.
   * @param {String} className - the name of the class to used by the query. This information is needed in order to
   * register proper storage events.
   * @param {Object} options - optional settings for the query.
   * @param {Boolean} options.debounce - whether updates or new instances events are debounces to prevent too many requests.
   * (Default: false)
   * @param {Number} options.debounceWait - the wait time, in milliseconds, for debouncing. (Default: 100)
   * @param {Number} options.debounceMaxWait - the maximum time, in milliseconds, the callback is to be delayed. (Default: not set).
   * @param {Boolean} options.useCreateMany - hooks to 'storage.createMany' events instead of 'storage.create'
   * @param {Function} options.processItemsFn - function to process ALL items after events
   * @param {Function} options.mapInstanceKeysFn - function used to process the ID of each instance or group
   * (for aggregate queries, such as counts or stats). (Default: _.identity)
   * @param {Array} ...queryParams -  optional parameters to pass to the query.
   *
   * @return {Observable} observable - an observable that can be subscribed to for updates instances.
   *
   * @example
   * const data$ = ContentObserver.observeAggregateQuery(
   *   myBee,
   *   'test',
   *   'countsByAge',
   *   'data',
   *   { debounce: true, debounceWait: 5000 },
   *   18,
   *   65);
   *
   * data$.subscribe(
   *   (items) => {
   *     // receive all the items always. Subsequent calls will contain the changed data
   *     console.log('items is an array of all items', items);
   *     console.log('item.status is the status of the current query', items.status)
   *   }
   * );
   */
  observeAggregateQuery,

  /**
   * @function observeFilteredQuery
   * @memberof ContentObserver
   * @description Observes instances based on a series of queries with conditions when to use.  Based on the parameters
   * used, the first query where allowed(...) returns true will be used. If none return true, the last query is used.
   *
   * @param {Object} bee - the bee object to use.
   * @param {String} namespace - the namespace of the query to observe.
   * @param {String} className - the name of the class to used by the query. This information is needed in order to
   * register proper storage events.
   * @param {Query[]} queries - an array of queries (name, when to use, and what parameters to user)
   * @param {String} Query.queryName - the name of the query to use. Must be in the same namespace as the class it returns.
   * @param {Function} Query.allowed - a function that returns if the current query entry can be used based on its parameters. Default returns true.
   * @param {Function} Query.parameters - a function that returns the parameters to use with the query. Default is an empty array.
   * @param {Object} options - optional settings for the query.
   * @param {Boolean} options.debounce - whether updates or new instances events are debounces to prevent too many requests.
   * (Default: false)
   * @param {Number} options.debounceWait - the wait time, in milliseconds, for debouncing. (Default: 100)
   * @param {Number} options.debounceMaxWait - the maximum time, in milliseconds, the callback is to be delayed. (Default: not set).
   * @param {Boolean} options.debounceChangeParams - whether changeParameters is debounced to prevent too many requests.
   * (Default: false)
   * @param {Number} options.debounceChangeParamsWait - the wait time, in milliseconds, for debouncing. (Default: 100)
   * @param {Boolean} options.useCreateMany - hooks to 'storage.createMany' events instead of 'storage.create'
   * @param {Function} options.processItemsFn - function to process ALL items after events
   * @param {Function} options.mapInstanceKeysFn - function used to process the ID of each instance or group
   * (for aggregate queries, such as counts or stats).
   * @param {Array} ...queryParams - optional parameters to pass for the *initial* query.
   *
   * @return {Observable} observable - an observable that can be subscribed to for updates instances.
   * @return {Function} observable.changeParameters - a function to change the parameters of the query.
   *
   * @example
   *   const data$ = ContentObserver.observeFilteredQuery(
   *  bee,
   *  'test',
   *  'data',
   *  [
   *    {
   *      queryName: 'byAge',
   *      allowed: (minAge, maxAge) => _.isNumber(minAge) && _.isNumber(maxAge),
   *      parameters: (minAge, maxAge) => [ minAge, maxAge ],
   *    },
   *    {
   *      queryName: 'byMinAge',
   *      allowed: (minAge) => _.isNumber(minAge),
   *      parameters: (minAge) => [ minAge ],
   *    },
   *    {
   *      queryName: 'byName',
   *      allowed: (name) => _.isString(name),
   *      parameters: (name) => [ name ],
   *    },
   *    {
   *      queryName: 'allByName',
   *      allowed: () => true,
   *      parameters: () => [],
   *    },
   *  ],
   *  {
   *    debounce: true,
   *    debounceWait: 2000,
   *    debounceChangeParams: true,
   *    debounceChangeParamsWait: 2000
   *  },
   *  18,
   *  65
   * );
   *
   * data$.subscribe(
   *   (items) => {
   *     // receive all the items always. Subsequent calls will contain the changed data
   *     console.log('items is an array of all items', items);
   *     console.log('item.status is the status of the current query', items.status)
   *   }
   * );
   *
   *
   * // To change the query
   * data$.changeParameters("User Name");
   * // Will internally switch to the "byName" query and pass "User Name" as the parameter to the new query.
   * // The data$ observable and any subscription will continue working as if it is a single query.
   */
  observeFilteredQuery,

  /**
   * @function observeFilteredAggregateQuery
   * @memberof ContentObserver
   * @description Observes instances based on a series of aggregate queries (counts, stats) with conditions when to use.  Based on the parameters
   * used, the first query where allowed(...) returns true will be used. If none return true, the last query is used.
   *
   * @param {Object} bee - the bee object to use.
   * @param {String} namespace - the namespace of the query to observe.
   * @param {String} className - the name of the class to used by the query. This information is needed in order to
   * register proper storage events.
   * @param {Query[]} queries - an array of queries (name, when to use, and what parameters to user)
   * @param {String} Query.queryName - the name of the query to use. Must be in the same namespace as the class it returns.
   * @param {Function} Query.allowed - a function that returns if the current query entry can be used based on its parameters. Default returns true.
   * @param {Function} Query.parameters - a function that returns the parameters to use with the query. Default is an empty array.
   * @param {Object} options - optional settings for the query.
   * @param {Boolean} options.debounce - whether updates or new instances events are debounces to prevent too many requests.
   * (Default: false)
   * @param {Number} options.debounceWait - the wait time, in milliseconds, for debouncing. (Default: 100)
   * @param {Number} options.debounceMaxWait - the maximum time, in milliseconds, the callback is to be delayed. (Default: not set).
   * @param {Boolean} options.debounceChangeParams - whether changeParameters is debounced to prevent too many requests.
   * (Default: false)
   * @param {Number} options.debounceChangeParamsWait - the wait time, in milliseconds, for debouncing. (Default: 100)
   * @param {Boolean} options.useCreateMany - hooks to 'storage.createMany' events instead of 'storage.create'
   * @param {Function} options.processItemsFn - function to process ALL items after events
   * @param {Function} options.mapInstanceKeysFn - function used to process the ID of each instance or group
   * (for aggregate queries, such as counts or stats). (Default: _.identity)
   * @param {Array} ...queryParams - optional parameters to pass for the *initial* query.
   *
   * @return {Observable} observable - an observable that can be subscribed to for updates instances.
   * @return {Function} observable.changeParameters - a function to change the parameters of the query.
   *
   * @example
   *   const data$ = ContentObserver.observeFilteredAggregateQuery(
   *  bee,
   *  'test',
   *  'data',
   *  [
   *    {
   *      queryName: 'countsByAge',
   *      allowed: (minAge, maxAge) => _.isNumber(minAge) && _.isNumber(maxAge),
   *      parameters: (minAge, maxAge) => [ minAge, maxAge ],
   *    },
   *    {
   *      queryName: 'countsByMinAge',
   *      allowed: (minAge) => _.isNumber(minAge),
   *      parameters: (minAge) => [ minAge ],
   *    },
   *    {
   *      queryName: 'countsByName',
   *      allowed: (name) => _.isString(name),
   *      parameters: (name) => [ name ],
   *    },
   *    {
   *      queryName: 'allcountsByName',
   *      allowed: () => true,
   *      parameters: () => [],
   *    },
   *  ],
   *  {
   *    debounce: true,
   *    debounceWait: 2000,
   *    debounceChangeParams: true,
   *    debounceChangeParamsWait: 2000
   *  },
   *  18,
   *  65
   * );
   *
   * data$.subscribe(
   *   (items) => {
   *     // receive all the items always. Subsequent calls will contain the changed data
   *     console.log('items is an array of all items', items);
   *     console.log('item.status is the status of the current query', items.status)
   *   }
   * );
   *
   *
   * // To change the query
   * data$.changeParameters("User Name");
   * // Will internally switch to the "byName" query and pass "User Name" as the parameter to the new query.
   * // The data$ observable and any subscription will continue working as if it is a single query.
   */
  observeFilteredAggregateQuery,

  /**
   * @enum {string}
   * @memberof ContentObserver
   * @description Constants for the various query statuses
   */
  status: {
    /**
     * @description When the query observable has been created or unsubscribed.
     */
    INIT: STATUS_INIT,

    /**
     * @description When a query is currently in progress
     */
    PENDING: STATUS_PENDING,

    /**
     * @description When a query has been completed.
     */
    DONE: STATUS_DONE,
  }
};
